import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import { useToast } from '@/hooks/useToast';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Upload, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type { SubscriptionPlanRequest, BulkImportResponse } from '@/types/subscription';

interface ParsedPlan extends SubscriptionPlanRequest {
  _validationStatus?: 'valid' | 'invalid';
  _errors?: string[];
}

export const SubscriptionPlanImportPage: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [parsedPlans, setParsedPlans] = useState<ParsedPlan[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<BulkImportResponse | null>(null);
  const [idempotent, setIdempotent] = useState(true);

  const canImport = hasPermission('create', 'subscriptionPlan');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };

  const parseFile = async (file: File) => {
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      
      // Handle both array and object with plans property
      const plans: SubscriptionPlanRequest[] = Array.isArray(json) ? json : json.plans || [];
      
      // Validate each plan
      const validated: ParsedPlan[] = plans.map((plan) => {
        const errors: string[] = [];
        
        if (!plan.planCode) errors.push('planCode is required');
        if (!plan.displayName) errors.push('displayName is required');
        if (!plan.description) errors.push('description is required');
        if (plan.monthlyPrice === undefined || plan.monthlyPrice < 0) errors.push('monthlyPrice must be >= 0');
        if (plan.yearlyPrice === undefined || plan.yearlyPrice < 0) errors.push('yearlyPrice must be >= 0');
        if (!plan.billingCycle) errors.push('billingCycle is required');
        if (!plan.planType) errors.push('planType is required');
        if (!plan.currency) errors.push('currency is required');
        if (!plan.status) errors.push('status is required');
        
        if (plan.planCode && !/^[A-Z0-9_-]+$/.test(plan.planCode)) {
          errors.push('planCode must match pattern ^[A-Z0-9_-]+$');
        }
        if (plan.currency && !/^[A-Z]{3}$/.test(plan.currency)) {
          errors.push('currency must be a 3-letter ISO code');
        }
        if (plan.trialEnabled && (!plan.trialDurationDays || plan.trialDurationDays < 1 || plan.trialDurationDays > 365)) {
          errors.push('trialDurationDays must be 1-365 when trialEnabled is true');
        }

        return {
          ...plan,
          _validationStatus: errors.length === 0 ? 'valid' : 'invalid',
          _errors: errors,
        };
      });

      setParsedPlans(validated);
      setImportResult(null);
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to parse JSON file',
      });
      setParsedPlans([]);
    }
  };

  const handleImport = async () => {
    if (parsedPlans.length === 0) return;
    
    const validPlans = parsedPlans.filter((p) => p._validationStatus === 'valid');
    if (validPlans.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No valid plans to import',
      });
      return;
    }

    try {
      setIsImporting(true);
      const result = await subscriptionPlanApi.bulkImport({
        plans: validPlans.map(({ _validationStatus, _errors, ...plan }) => plan),
        idempotent,
      });
      
      setImportResult(result);
      toast({
        variant: 'success',
        title: 'Success',
        description: `Imported ${result.imported} plan(s), skipped ${result.skipped}`,
      });
      
      if (result.errors.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Import Warnings',
          description: `${result.errors.length} plan(s) had errors`,
        });
      }
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to import plans',
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/subscription-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Upload className="h-8 w-8" />
              Bulk Import Plans
            </h1>
            <p className="text-muted-foreground mt-2">Import multiple subscription plans from JSON file</p>
          </div>
        </div>
      </div>

      {!canImport && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="pt-6">
            <p className="text-yellow-800 dark:text-yellow-200">
              You do not have permission to import plans.
            </p>
          </CardContent>
        </Card>
      )}

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Upload JSON File</CardTitle>
          <CardDescription>Select a JSON file containing subscription plans</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              disabled={!canImport}
              className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Expected format: Array of plan objects or object with "plans" property
            </p>
          </div>

          {file && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" />
              <span>{file.name}</span>
              <span className="text-muted-foreground">({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Table */}
      {parsedPlans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Preview ({parsedPlans.length} plans)</CardTitle>
            <CardDescription>Review parsed plans before importing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Valid: {parsedPlans.filter((p) => p._validationStatus === 'valid').length} / Invalid:{' '}
                  {parsedPlans.filter((p) => p._validationStatus === 'invalid').length}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={idempotent}
                    onChange={(e) => setIdempotent(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label className="text-sm">Idempotent (skip duplicates)</Label>
                </div>
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Plan Code</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Plan Type</TableHead>
                      <TableHead>Monthly Price</TableHead>
                      <TableHead>Yearly Price</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedPlans.map((plan, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {plan._validationStatus === 'valid' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{plan.planCode || 'N/A'}</TableCell>
                        <TableCell>{plan.displayName || 'N/A'}</TableCell>
                        <TableCell>{plan.planType || 'N/A'}</TableCell>
                        <TableCell>{plan.monthlyPrice ?? 'N/A'}</TableCell>
                        <TableCell>{plan.yearlyPrice ?? 'N/A'}</TableCell>
                        <TableCell>
                          {plan._errors && plan._errors.length > 0 ? (
                            <ul className="text-xs text-red-600 list-disc list-inside">
                              {plan._errors.map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-xs text-green-600">Valid</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setFile(null);
                  setParsedPlans([]);
                  setImportResult(null);
                }}>
                  Clear
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={isImporting || parsedPlans.filter((p) => p._validationStatus === 'valid').length === 0}
                >
                  {isImporting ? 'Importing...' : 'Import Valid Plans'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResult && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                  <div className="text-sm text-muted-foreground">Imported</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{importResult.skipped}</div>
                  <div className="text-sm text-muted-foreground">Skipped</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">{importResult.errors.length}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div>
                  <Label>Error Details</Label>
                  <div className="mt-2 space-y-2">
                    {importResult.errors.map((error, index) => (
                      <div key={index} className="p-2 rounded-md bg-red-50 dark:bg-red-900/20">
                        <div className="font-mono text-sm">{error.planCode}</div>
                        <div className="text-xs text-red-600">{error.error}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button asChild>
                  <Link to="/admin/subscription-plans">View Plans</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionPlanImportPage;
